import React, { useEffect, useState } from "react";
import data from "../../assets/CustomerKPIData/avg-amount-per-ticket-json";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip as ToolTIP,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { PageHeader, PageShell } from "../../components/UI/Page";
import Chart from "chart.js/auto";
import { Bar, Doughnut, Line, Pie, PolarArea, Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  ArrowCircleLeftOutlined,
  ArrowCircleRight,
  ArrowCircleRightOutlined,
  ArrowLeft,
} from "@mui/icons-material";
import { useIsDarkMode } from "../../theme/ThemeContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DataTable = ({ data }) => {
  const isDark = useIsDarkMode();
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [fetchedData, setFetchedData] = useState({ Results: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSummaryDialog, setSummaryOpenDialog] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10); // New state for items per page
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedComparison, setSelectedComparison] = useState("Ticket Count");
  const comparisonOptions = ["Ticket Count", "Avg $ per Ticket", "Total Sales"];

  const handleComparisonChange = (event) => {
    setSelectedComparison(event.target.value);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const visibleData = fetchedData?.Results?.slice(startIndex, endIndex) || [];

  const totalPages = Math.ceil(
    (fetchedData?.Results?.length || 0) / itemsPerPage
  );

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    const maxPage = Math.ceil(
      (searchResults?.length || fetchedData?.Results?.length || 0) /
        itemsPerPage
    );
    setCurrentPage(Math.min(nextPage, maxPage));
  };

  const handlePrevPage = () => {
    const prevPage = currentPage - 1;
    setCurrentPage(Math.max(prevPage, 1));
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  const renderArrow = (column) => {
    if (sortedColumn === column) {
      return sortOrder === "asc" ? "↑" : "↓";
    }
    return null;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  /* Chart.js axes default to light-only colors, so they take theirs from
     the in-app toggle. The dialogs themselves follow the MUI theme. */
  const chartTickColor = isDark ? "#cbd5e1" : "#334155";
  const chartGridColor = isDark
    ? "rgba(148, 163, 184, 0.2)"
    : "rgba(100, 116, 139, 0.2)";

  const columns = [
    "Customer",
    "CustomerID",
    "Branch",
    "Sales Rep",
    "LY Ticket Count",
    "YTD Ticket Count",
    "LY Avg $ per Ticket",
    "Avg $ per Ticket",
    "TotalSalesLY",
    "TotalSalesYTD",
  ];

  useEffect(() => {
    setFetchedData(data || []);
  }, [data]);

  const sortData = (column) => {
    const isAsc = sortedColumn === column && sortOrder === "asc";
    const sortedData = fetchedData.Results.slice().sort((a, b) => {
      const aValue = String(a[column]).toLowerCase();
      const bValue = String(b[column]).toLowerCase();

      return isAsc
        ? aValue.localeCompare(bValue, undefined, { numeric: true })
        : bValue.localeCompare(aValue, undefined, { numeric: true });
    });

    setSortedColumn(column);
    setSortOrder(isAsc ? "desc" : "asc");
    setFetchedData({ Results: sortedData });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClickRow = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleSummaryClick = () => {
    setSummaryOpenDialog(true);
  };

  const handleSummaryCloseDialog = () => {
    setSummaryOpenDialog(false);
  };

  useEffect(() => {
    // Filter data based on the search term
    const results = fetchedData?.Results?.filter((row) =>
      row.Customer.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results);
  }, [searchTerm, fetchedData]);

  const chartData = {
    labels: ["LY Ticket Count", "YTD Ticket Count"],
    datasets: [
      {
        label: "Ticket Count",
        data: [
          selectedRow ? String(selectedRow["LY Ticket Count"]) : "0",
          selectedRow ? String(selectedRow["YTD Ticket Count"]) : "0",
        ],
        backgroundColor: ["rgba(255,99,132,0.6)", "rgba(75,192,192,0.6)"],
        borderColor: ["rgba(255,99,132,1)", "rgba(75,192,192,1)"],
        borderWidth: 1,
      },
    ],
  };

  const avgPriceData = {
    labels: ["LY Avg $ per Ticket", "Avg $ per Ticket"],
    datasets: [
      {
        label: "Avg $ per Ticket Comparison",
        data: [
          selectedRow ? selectedRow["LY Avg $ per Ticket"] : 0,
          selectedRow ? selectedRow["Avg $ per Ticket"] : 0,
        ],
        backgroundColor: ["rgba(255,99,132,0.6)", "rgba(75,192,192,0.6)"],
        borderColor: ["rgba(255,99,132,1)", "rgba(75,192,192,1)"],
        borderWidth: 1,
        formatter: formatCurrency,
      },
    ],
  };

  const totalSalesData = {
    labels: ["Total Sales LY", "Total Sales YTD"],
    datasets: [
      {
        label: "Total Sales Comparison",
        data: [
          selectedRow ? selectedRow["TotalSalesLY"] : 0,
          selectedRow ? selectedRow["TotalSalesYTD"] : 0,
        ],
        backgroundColor: ["rgba(255,99,132,0.6)", "rgba(75,192,192,0.6)"],
        borderColor: ["rgba(255,99,132,1)", "rgba(75,192,192,1)"],
        borderWidth: 1,
        formatter: formatCurrency,
      },
    ],
  };

  const ticketSummaryChartData = {
    labels: visibleData.map((data) => data?.Customer || []),
    datasets: [
      {
        label: "LY Ticket Count",
        data: visibleData.map((data) => data["LY Ticket Count"] || 0),
        backgroundColor: "rgba(255,99,132,0.6)",
        borderColor: "rgba(255,99,132,1)",
        borderWidth: 1,
      },
      {
        label: "YTD Ticket Count",
        data: visibleData.map((data) => data["YTD Ticket Count"] || 0),
        backgroundColor: "rgba(75,192,192,0.6)",
        borderColor: "rgba(75,192,192,1)",
        borderWidth: 1,
      },
    ],
  };

  const avg$SummaryChartData = {
    labels: visibleData.map((data) => data?.Customer || []),
    datasets: [
      {
        label: "LY Avg $ per Ticket",
        data: visibleData.map((data) => data["LY Avg $ per Ticket"] || 0),
        backgroundColor: "rgba(255,99,132,0.6)",
        borderColor: "rgba(255,99,132,1)",
        borderWidth: 1,
        formatter: formatCurrency,
      },
      {
        label: "Avg $ per Ticket",
        data: visibleData.map((data) => data["Avg $ per Ticket"] || 0),
        backgroundColor: "rgba(75,192,192,0.6)",
        borderColor: "rgba(75,192,192,1)",
        borderWidth: 1,
        formatter: formatCurrency,
      },
    ],
  };

  const totalSalesSummaryChartData = {
    labels: visibleData.map((data) => data?.Customer || []),
    datasets: [
      {
        label: "Total Sales LY",
        data: visibleData.map((data) => data["TotalSalesLY"] || 0),
        backgroundColor: "rgba(255,99,132,0.6)",
        borderColor: "rgba(255,99,132,1)",
        borderWidth: 1,
        formatter: formatCurrency,
      },
      {
        label: "Total Sales YTD",
        data: visibleData.map((data) => data["TotalSalesYTD"] || 0),
        backgroundColor: "rgba(75,192,192,0.6)",
        borderColor: "rgba(75,192,192,1)",
        borderWidth: 1,
        formatter: formatCurrency,
      },
    ],
  };

  const comparisonData = () => {
    switch (selectedComparison) {
      case "Ticket Count":
        return ticketSummaryChartData;
      case "Avg $ per Ticket":
        return avg$SummaryChartData;
      case "Total Sales":
        return totalSalesSummaryChartData;
      default:
        return {};
    }
  };

  const currencyColumns = [
    "Avg $ per Ticket",
    "TotalSalesYTD",
    "LY Avg $ per Ticket",
    "TotalSalesLY",
  ];
  const numericColumns = [
    ...currencyColumns,
    "LY Ticket Count",
    "YTD Ticket Count",
  ];

  const chartOptions = (formatY) => ({
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: { color: chartTickColor },
        grid: { color: chartGridColor },
      },
      y: {
        ticks: formatY
          ? { color: chartTickColor, callback: formatY }
          : { color: chartTickColor },
        grid: { color: chartGridColor },
      },
    },
  });

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <SearchRoundedIcon
            className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            sx={{ fontSize: 20 }}
          />
          <input
            type="text"
            placeholder="Search by customer"
            aria-label="Search by customer"
            value={searchTerm}
            onChange={handleSearch}
            className="app-input w-full rounded-xl border py-2.5 pl-10 pr-3 text-sm"
          />
        </div>
        {Array.isArray(searchResults) && (
          <span className="app-muted text-sm tabular-nums">
            {searchResults.length.toLocaleString()} customer
            {searchResults.length === 1 ? "" : "s"}
          </span>
        )}
        {fetchedData && (
          <button
            type="button"
            className="app-btn app-btn-primary ml-auto"
            onClick={handleSummaryClick}
          >
            <BarChartRoundedIcon sx={{ fontSize: 18 }} />
            Full Summary
          </button>
        )}
      </div>

      <div className="app-card overflow-hidden rounded-2xl border">
        <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column}
                    aria-sort={
                      sortedColumn === column
                        ? sortOrder === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={
                      numericColumns.includes(column) ? "!text-right" : ""
                    }
                  >
                    <button
                      type="button"
                      onClick={() => sortData(column)}
                      className={`inline-flex items-center gap-1 whitespace-nowrap font-semibold transition-colors hover:text-[color:var(--app-text)] ${
                        sortedColumn === column
                          ? "text-[color:var(--app-accent)]"
                          : ""
                      }`}
                    >
                      {column} {renderArrow(column)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(searchResults) && fetchedData ? (
                searchResults?.map((row) => (
                  <tr
                    key={row.CustomerID}
                    onClick={() => handleClickRow(row)}
                    className="cursor-pointer"
                  >
                    {columns.map((column) => (
                      <td
                        key={column}
                        className={
                          numericColumns.includes(column)
                            ? "whitespace-nowrap text-right tabular-nums"
                            : column === "Customer"
                            ? "font-medium"
                            : ""
                        }
                      >
                        {/* Check if the column needs currency formatting */}
                        {currencyColumns.includes(column)
                          ? formatCurrency(row[column])
                          : row[column]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="app-muted !py-10 text-center">
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="kpi-customer-title"
      >
        <DialogTitle id="kpi-customer-title" sx={{ pb: 0.5 }}>
          {selectedRow ? `Customer: ${selectedRow.Customer}` : ""}
          <span className="app-muted mt-0.5 block text-sm font-medium">
            Last Year / Current YTD Comparison
          </span>
        </DialogTitle>
        <DialogContent>
          <div className="space-y-5 pt-2">
            <div>
              <p className="app-eyebrow mb-2">Ticket Count</p>
              <Bar data={chartData} options={chartOptions()} />
            </div>
            <div>
              <p className="app-eyebrow mb-2">Average $ Per Ticket</p>
              <Bar
                data={avgPriceData}
                options={chartOptions((value) =>
                  avgPriceData.datasets[0].formatter(value)
                )}
              />
            </div>
            <div>
              <p className="app-eyebrow mb-2">Total Sales</p>
              <Bar
                data={totalSalesData}
                options={chartOptions((value) =>
                  totalSalesData.datasets[0].formatter(value)
                )}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {fetchedData && (
        <Dialog
          open={openSummaryDialog}
          onClose={handleSummaryCloseDialog}
          fullWidth
          maxWidth="xl"
          aria-labelledby="kpi-summary-title"
        >
          <DialogTitle id="kpi-summary-title" sx={{ pb: 1 }}>
            Last Year / Current YTD Comparison:{" "}
            <span className="app-accent">
              {selectedComparison === "Ticket Count"
                ? "Ticket Count"
                : selectedComparison === "Avg $ per Ticket"
                ? "Average $ per Ticket"
                : selectedComparison === "Total Sales"
                ? "Total Sales"
                : ""}
            </span>
          </DialogTitle>
          <DialogContent>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm">
                <span className="app-muted font-medium">Customer Per Page:</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="app-input w-24 rounded-xl border py-1.5 pl-3 pr-8 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="app-muted font-medium">Chart Data:</span>
                <select
                  value={selectedComparison}
                  onChange={handleComparisonChange}
                  className="app-input w-56 rounded-xl border py-1.5 pl-3 pr-8 text-sm"
                >
                  {comparisonOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <Bar
              data={comparisonData()}
              options={chartOptions((value) => {
                if (selectedComparison !== "Ticket Count") {
                  return avgPriceData.datasets[0].formatter(value);
                } else {
                  return value;
                }
              })}
            />
          </DialogContent>
          <div className="flex items-center justify-center gap-2 pb-4">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
              className="app-icon-btn disabled:pointer-events-none disabled:opacity-40"
            >
              <ArrowCircleLeftOutlined style={{ fontSize: "30px" }} />
            </button>
            <p className="text-sm font-medium tabular-nums">
              Page {currentPage} of {totalPages}
            </p>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next page"
              className="app-icon-btn disabled:pointer-events-none disabled:opacity-40"
            >
              <ArrowCircleRightOutlined style={{ fontSize: "30px" }} />
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

const CustomerKPIs = () => {
  const [fetchedData, setFetchedData] = useState([]);

  useEffect(() => {
    setFetchedData(data || []);
  }, []);

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="2xl">
        <PageHeader
          icon={InsightsRoundedIcon}
          tone="violet"
          eyebrow="Everyday Tools"
          title="Customer KPIs"
          description="Ticket counts, average ticket and total sales by customer — last year vs. year to date. Click a row for that customer's charts."
        />
        <DataTable data={fetchedData} />
      </PageShell>
    </div>
  );
};

export default CustomerKPIs;
